<template>
  <div>
    <v-data-table
      :items="products"
      :loading="isLoading"
      :headers="productHeaders"
      :hide-default-footer="small"
      :items-per-page="small ? 1000: 10"
      :footer-props="{ itemsPerPageOptions : small ? [1000]: [10, 50] }"
      item-key="id"
      single-expand
      no-data-text="No Products added yet"
    >
      <template v-slot:item="props">
        <tr>
          <td class="py-2">
            {{ props.item.description }}
          </td>

          <td>{{ props.item.vendorReferenceCode }}</td>

          <td>{{ props.item.retailerCode }}</td>

          <td class="py-2">
            <span
              v-if="props.item.weight"
            >{{ props.item.weight.value }} {{ weightUnits[props.item.weight.units].text }}</span>
            <span v-else>N/A</span>
          </td>

          <td>{{ props.item.unitsOnPallet }}</td>

          <td>{{ getAggregatePalletWeight(props.item) }}</td>

          <td>{{ props.item.isTopper }}</td>

          <td>{{ props.item.isTopable }}</td>

          <td class="pr-0 text-center">
            <v-btn
              icon
              @click="openDeleteProductDialog(props.item)"
            >
              <v-icon color="grey">
                mdi-delete-outline
              </v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <confirmation-dialog
      v-if="deleteProductDialog.isOpen"
      :color="deleteProductDialog.color"
      :subtitle="deleteProductDialog.subtitle"
      :is-accept-button-loading="deleteProductDialog.isAcceptButtonLoading"
      @accept="deleteProduct"
      @close="resetDeleteProductDialog"
    >
      <v-divider />
      <div class="px-3 red lighten-5">
        <v-row>
          <v-col
            sm="4"
            xs="12"
          >
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="caption primary--text font-weight-bold">
                  Product Name
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ deleteProductDialog.details.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col
            sm="4"
            xs="12"
          >
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="caption primary--text font-weight-bold">
                  Vendor reference code
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ deleteProductDialog.details.vendorReferenceCode }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col
            sm="4"
            xs="12"
          >
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="caption primary--text font-weight-bold">
                  Units on Pallet
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ deleteProductDialog.details.unitsOnPallet }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-col>
        </v-row>
      </div>

      <v-divider />
    </confirmation-dialog>
  </div>
</template>

<script>
import { products } from '@/endpoints';
import { dimensionUnits, weightUnits } from '@/constants/misc';
import ConfirmationDialog from '@/components/commons/ConfirmationDialog.vue';

const smallHeaders = [
  {
    text: 'Product Name',
    align: 'start',
    value: 'name',
  },
  { text: 'SKU ID', value: 'sku' },
  { text: 'Product Dimensions', value: 'dim' },
  { text: 'Weight', value: 'weight' },
  { text: 'QTY', value: 'qty' },
  { text: 'Is Topper', value: 'isTopper', sortable: false },
  { text: 'Is Toppable', value: 'isTopable', sortable: false },
  { text: 'Actions', align: 'center', sortable: false },
];

const fullHeaders = [
  {
    text: 'Product Name',
    align: 'start',
    value: 'description',
  },
  { text: 'Vendor reference code', value: 'vendorReferenceCode' },
  { text: 'Retailer code', value: 'retailerCode' },
  { text: 'Weight', value: 'weight', sortable: false },
  { text: 'Units on Pallet', value: 'unitsOnPallet' },
  { text: 'Full Pallet Weight', value: 'qty', sortable: false },
  { text: 'Is Topper', value: 'isTopper', sortable: false },
  { text: 'Is Toppable', value: 'isTopable', sortable: false },
  { text: 'Actions', align: 'center', sortable: false },
];

function defaultDeleteDialog() {
  return {
    color: '',
    details: {},
    subtitle: '',
    isOpen: false,
    isAcceptButtonLoading: false,
  };
}

export default {
  components: { ConfirmationDialog },
  props: {
    products: {
      type: Array,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      weightUnits,
      dimensionUnits,
      productHeaders: [],
      deleteProductDialog: defaultDeleteDialog(),
    };
  },
  mounted() {
    this.productHeaders = this.small ? smallHeaders : fullHeaders;
  },
  methods: {
    deleteProduct() {
      this.deleteProductDialog.isAcceptButtonLoading = true;

      const url = `${products}${this.deleteProductDialog.details.id}/`;
      this.axios
        .delete(url)
        .then(() => {
          this.$emit('displaySnackbar', {
            color: 'green',
            text: 'Successfully deleted product',
          });

          this.resetDeleteProductDialog();
        })
        .catch(() => {
          this.deleteProductDialog.isAcceptButtonLoading = false;
          this.$emit('displaySnackbar', {
            color: 'red',
            text: 'An error occured. Please try again',
          });
        });
    },
    getAggregatePalletWeight(product) {
      const productQuantity = product && product.unitsOnPallet;

      const palletWeight = product && product.pallet && product.pallet.emptyWeight;

      const productWeight = product && product.weight;

      if (!palletWeight && !productWeight) {
        return '';
      }

      if (!palletWeight && productWeight) {
        return `${productWeight.value} ${productWeight.units}`;
      }

      if (palletWeight && !productWeight) {
        return `${palletWeight.value} ${palletWeight.units}`;
      }

      const totalWeight = palletWeight.value + productWeight.value * productQuantity;
      return `${totalWeight.toFixed(2)} ${productWeight.units}`;
    },
    openDeleteProductDialog(product) {
      const subtitle = 'Are you sure you want to delete this product?';

      this.deleteProductDialog = {
        ...this.deleteProductDialog,
        subtitle,
        isOpen: true,
        color: 'error',
        details: product,
      };
    },
    resetDeleteProductDialog() {
      this.deleteProductDialog = defaultDeleteDialog();
    },
  },
};
</script>
